import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import { dataService } from "../../../Services/data/data.service";
import { Box, Dialog, DialogActions, DialogTitle, DialogContent, Tooltip } from "@mui/material";
import { Trash2, X, RefreshCw } from "lucide-react";
import { Spin } from "react-cssfx-loading";
import { toast } from "react-toastify";
import { GraphFilterTypes, resultColumns, valueTypeCodes, externalApiTypes, controlTypes } from "../../../Config/constants";
import MeasurementsTable from "./Components/MeasurementsTable";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import HeaderEnd from "../../Shared/Header/HeaderEnd";
import Button from "../../Shared/Buttons/Button";

const formatSecondsAsClock = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined) return "";
  const safeSeconds = Math.max(0, Number(totalSeconds));
  if (Number.isNaN(safeSeconds)) return "";
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = Math.floor(safeSeconds % 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getMeasurementValue = (typeId, externalApiTypeId, text, int, time, dataListValue, imageLink, externalApiValue, isNegative, numericValue) => {
  switch (typeId) {
    case 1: return int;
    case 2:
      if (int !== null && int !== undefined && int >= 0) return time || formatSecondsAsClock(int);
      return "";
    case 3: return text;
    case 4: return dataListValue;
    case 5: return text;
    case 6: return imageLink;
    case 7:
      if (externalApiTypeId === externalApiTypes.STOPWATCH) {
        if (int !== null && int !== undefined && int >= 0) return time || formatSecondsAsClock(int);
        return "";
      } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
        return isNegative ? `-${numericValue}` : numericValue;
      }
      return text;
    default: return "";
  }
};

const getCommentText = (typeId, externalApiTypeId, text, commentText) => {
  if (typeId === 7) {
    if (externalApiTypeId === externalApiTypes.STOPWATCH) return text;
    if (externalApiTypeId === externalApiTypes.NUMERIC) return text;
    if (externalApiTypeId === externalApiTypes.TEXT) return commentText;
    return commentText;
  }
  return commentText ?? "";
};

const mapMeasurementToRow = (measurement, t, valueTypeCodes) => ({
  accountId: measurement.accountId,
  accountName: measurement.accountName,
  accountCategoryId: measurement.accountCategoryId,
  accountCategoryName: measurement.accountCategoryName,
  checked: false,
  projectId: measurement.projectId,
  toDate: measurement.toDate,
  fromDate: measurement.fromDate,
  color: measurement.color,
  id: measurement.id,
  typeId: measurement.typeId,
  valueTypeId: measurement.valueTypeId,
  companyId: measurement.companyId,
  qrCode: measurement.qrCode,
  companyName: measurement.companyName,
  detailId: measurement.projectDetailId,
  projectName: measurement.projectName,
  resourceName: measurement.resourceName,
  description: measurement.description,
  startTime: measurement.startTime,
  stopTime: measurement.stopValue,
  insertedOn: new Date(measurement.insertedOn),
  updatedOn: measurement.stopTime,
  measurementSessionId: measurement.measurementSessionId,
  measurementValue: getMeasurementValue(
    measurement.typeId,
    measurement.externalApiTypeId,
    measurement.textValue,
    measurement.integerValue,
    measurement.dateValue,
    measurement.dataListValue,
    measurement.imageLink,
    measurement.externalApiValues,
    measurement.isNegative,
    measurement.numericValue
  ),
  integerValue: measurement.integerValue,
  numericValue: measurement.numericValue,
  running: measurement.running,
  thumbnail: measurement.thumbnail,
  imageId: measurement.imageId,
  commentText: getCommentText(measurement.typeId, measurement.externalApiTypeId, measurement.textValue, measurement.commentText),
  commentImage: measurement.commentImage,
  commentId: measurement.commentId,
  valueType: (() => {
    switch (measurement.valueTypeId) {
      case valueTypeCodes.VA:  return t('dataManagement.valueAdded');
      case valueTypeCodes.NVA: return t('dataManagement.noValueAdded');
      case valueTypeCodes.W:   return t('dataManagement.waste');
      default:                 return t('dataManagement.unknown');
    }
  })(),
  keyValue: measurement.keyValue,
  signedEventPrice: measurement.eventPrice,
  externalApiTypeId: measurement.externalApiTypeId,
  isNegative: measurement.isNegative,
  dataListId: measurement.dataListId,
  sessionValues: measurement.metadata ? JSON.parse(measurement.metadata).map(m => m.value).join(', ') : '',
  sessionMetadata: measurement.metadata ? JSON.parse(measurement.metadata) : [],
});

function DataManagementPage(props) {
  const { t } = useTranslation();
  const classes = useStyles();

  // ── Server-side pagination / sort / filter state ──────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState([{ columnName: 'insertedOn', direction: 'desc' }]);
  const [serverFilters, setServerFilters] = useState([]);

  // ── Row data ──────────────────────────────────────────────────────────────
  const [measurementRows, setMeasurementRows] = useState([]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selection, setSelection] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false);

  // ── Delete ────────────────────────────────────────────────────────────────
  const [measurementsToDelete, setMeasurementsToDelete] = useState([]);
  const [isSelectAllDelete, setIsSelectAllDelete] = useState(false);
  const [deleteMeasurementsDialogOpen, setDeleteMeasurementsDialogOpen] = useState(false);

  // ── Loading ───────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // ── Filter debounce ───────────────────────────────────────────────────────
  const filterDebounceRef = useRef(null);

  // ── Extract server-side filter values ─────────────────────────────────────
  const getFilterValue = (filters, col) => filters.find(f => f.columnName === col)?.value ?? null;

  // ── Core fetch ────────────────────────────────────────────────────────────
  const fetchPage = useCallback(async ({
    page, size, sort, filters, showSpinner = false
  } = {}) => {
    const pg   = page    ?? currentPage;
    const sz   = size    ?? pageSize;
    const sr   = sort    ?? sorting;
    const sf   = filters ?? serverFilters;

    const sortColumn    = sr.length > 0 ? sr[0].columnName : 'insertedOn';
    const sortDirection = sr.length > 0 ? sr[0].direction  : 'desc';

    if (showSpinner) setLoading(true);
    try {
      const result = await dataService.getAllMeasurementsByUserIdPaged(
        props.Login.userid, pg * sz, sz, sortColumn, sortDirection,
        getFilterValue(sf, 'companyName'),
        getFilterValue(sf, 'projectName'),
        getFilterValue(sf, 'resourceName'),
        getFilterValue(sf, 'description')
      );
      if (!result) return;
      setMeasurementRows(result.rows.map(m => mapMeasurementToRow(m, t, valueTypeCodes)));
      setTotalCount(result.totalCount);
    } catch {
      toast.error(t('dataManagement.fetchError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [currentPage, pageSize, sorting, serverFilters, props.Login.userid, t]);

  // Initial load
  useEffect(() => {
    fetchPage({ showSpinner: true });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination handlers ───────────────────────────────────────────────────
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setSelection([]);
    setSelectAllMode(false);
    fetchPage({ page });
  }, [fetchPage]);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(0);
    setSelection([]);
    setSelectAllMode(false);
    fetchPage({ page: 0, size });
  }, [fetchPage]);

  // ── Sort handler ──────────────────────────────────────────────────────────
  const handleSortingChange = useCallback((newSorting) => {
    setSorting(newSorting);
    setCurrentPage(0);
    setSelection([]);
    setSelectAllMode(false);
    fetchPage({ page: 0, sort: newSorting });
  }, [fetchPage]);

  // ── Filter handler (debounced) ────────────────────────────────────────────
  const handleFiltersChange = useCallback((newFilters) => {
    setServerFilters(newFilters);
    setCurrentPage(0);
    setSelection([]);
    setSelectAllMode(false);
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      fetchPage({ page: 0, filters: newFilters });
    }, 350);
  }, [fetchPage]);

  // ── Refresh ───────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchPage();
      setLastRefreshed(new Date());
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, fetchPage]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const onSelectionChange = (e) => {
    setSelection(e);
    if (selectAllMode) setSelectAllMode(false);
  };

  // ── Fetch all rows for export (lazy, only when user exports with selectAllMode) ──
  const fetchAllForExport = useCallback(async () => {
    const sortColumn    = sorting.length > 0 ? sorting[0].columnName : 'insertedOn';
    const sortDirection = sorting.length > 0 ? sorting[0].direction  : 'desc';
    const result = await dataService.getAllMeasurementsByUserIdPaged(
      props.Login.userid, 0, totalCount, sortColumn, sortDirection,
      getFilterValue(serverFilters, 'companyName'),
      getFilterValue(serverFilters, 'projectName'),
      getFilterValue(serverFilters, 'resourceName'),
      getFilterValue(serverFilters, 'description')
    );
    if (!result) return [];
    return result.rows.map(m => mapMeasurementToRow(m, t, valueTypeCodes));
  }, [props.Login.userid, totalCount, sorting, serverFilters, t]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteMeasurement = (measurementId) => {
    setMeasurementsToDelete([measurementId]);
    setIsSelectAllDelete(false);
    setDeleteMeasurementsDialogOpen(true);
  };

  const handleBatchDelete = () => {
    if (selectAllMode) {
      setIsSelectAllDelete(true);
      setMeasurementsToDelete([]);
    } else {
      setIsSelectAllDelete(false);
      setMeasurementsToDelete(selection.map(index => measurementRows[index]?.id).filter(Boolean));
    }
    setDeleteMeasurementsDialogOpen(true);
  };

  const deleteSelectedMeasurements = () => {
    if (isSelectAllDelete) {
      dataService.setMeasurementsInactiveByUserIdAndFilter(
        props.Login.userid,
        getFilterValue(serverFilters, 'companyName'),
        getFilterValue(serverFilters, 'projectName'),
        getFilterValue(serverFilters, 'resourceName'),
        getFilterValue(serverFilters, 'description')
      ).then(() => {
        toast.success(t('dataManagement.measurementsRemoved'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        setSelection([]);
        setSelectAllMode(false);
        handleCloseDeleteMeasurementsDialog();
        fetchPage({ showSpinner: false });
      }).catch(() => {
        toast.error(t('dataManagement.deleteError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      });
    } else {
      dataService.setMeasurementsInactiveByIdList(measurementsToDelete)
        .then(() => {
          toast.success(t('dataManagement.measurementsRemoved'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
          setSelection([]);
          setSelectAllMode(false);
          handleCloseDeleteMeasurementsDialog();
          fetchPage({ showSpinner: false });
        }).catch(() => {
          toast.error(t('dataManagement.deleteError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });
    }
  };

  const handleCloseDeleteMeasurementsDialog = () => {
    setDeleteMeasurementsDialogOpen(false);
    setMeasurementsToDelete([]);
    setIsSelectAllDelete(false);
  };

  const setMeasurementFinished = (measurementId) => {
    setMeasurementRows(prev => {
      const idx = prev.findIndex(x => x.id === measurementId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], running: false };
      return next;
    });
  };

  const deleteCount = isSelectAllDelete ? totalCount : measurementsToDelete.length;

  return (
    <React.Fragment>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
        </div>
      )}
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>{t('dataManagement.title')}</HeaderTitle>
          <HeaderEnd>
            {lastRefreshed && (
              <span className={classes.refreshTimestamp}>
                {t('dataManagement.lastUpdated', { time: lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
              </span>
            )}
            <Tooltip title={t('dataManagement.refresh')} placement="bottom">
              <span>
                <Button
                  iconButton
                  variant="secondary"
                  size="small"
                  startIcon={<RefreshCw className={isRefreshing ? classes.iconSpin : ''} />}
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                />
              </span>
            </Tooltip>
            <div className={classes.deleteButtonWrapper + ((selection.length > 0 || selectAllMode) ? ' ' + classes.deleteButtonWrapper__visible : '')}>
              <Button variant="destructive" size="small" startIcon={<Trash2 />} onClick={handleBatchDelete}>
                {selectAllMode
                  ? t('dataManagement.deleteAll', { count: totalCount })
                  : (selection.length > 0 ? t('dataManagement.deleteCount', { count: selection.length }) : t('common.delete'))}
              </Button>
            </div>
          </HeaderEnd>
        </HeaderBar>

        {selection.length > 0 && (
          <div className={classes.selectionBanner}>
            {selectAllMode ? (
              <>
                <span>{t('dataManagement.allMeasurementsSelected', { count: totalCount })}</span>
                <button className={classes.selectionBanner__link} onClick={() => { setSelectAllMode(false); setSelection([]); }}>
                  {t('dataManagement.clearSelection')}
                </button>
              </>
            ) : (
              <>
                <span>{t('dataManagement.measurementsSelectedOnPage', { count: selection.length })}</span>
                {selection.length === pageSize && totalCount > pageSize && (
                  <button className={classes.selectionBanner__link} onClick={() => setSelectAllMode(true)}>
                    {t('dataManagement.selectAll', { count: totalCount })}
                  </button>
                )}
                <button className={classes.selectionBanner__link} onClick={() => setSelection([])}>
                  {t('dataManagement.clearSelection')}
                </button>
              </>
            )}
          </div>
        )}

        <div className={classes.measurementsTableContainer}>
          <MeasurementsTable
            data={measurementRows}
            columns={resultColumns}
            onSelectionChange={onSelectionChange}
            selection={selection}
            selectAllMode={selectAllMode}
            handleDeleteMeasurement={handleDeleteMeasurement}
            fetchAllForExport={fetchAllForExport}
            setMeasurementFinished={setMeasurementFinished}
            tableId="data-management-measurements"
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            sorting={sorting}
            serverFilters={serverFilters}
            onCurrentPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSortingChange={handleSortingChange}
            onFiltersChange={handleFiltersChange}
            refreshMeasurements={fetchPage}
            {...props}
          />
        </div>
      </Box>

      {deleteMeasurementsDialogOpen && (
        <Dialog
          className={classes.dialog}
          open={deleteMeasurementsDialogOpen}
          onClose={handleCloseDeleteMeasurementsDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          classes={{ paper: classes.dialogPaper }}
        >
          <div className={classes.dialog__head}>
            <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
              {t('dataManagement.deleteMeasurements', { count: deleteCount })}
            </DialogTitle>
            <button onClick={handleCloseDeleteMeasurementsDialog} className={classes.dialog__closeButton}>
              <X size={19} />
            </button>
          </div>
          <DialogContent className={classes.dialog__contentContainer}>
            <div className={classes.dialog__content}>
              <p className={classes.dialog__subtitle}>
                {t('dataManagement.confirmDeleteMeasurements', { count: deleteCount })}
              </p>
            </div>
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
            <Button variant="outlined" onClick={handleCloseDeleteMeasurementsDialog}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" startIcon={<Trash2 />} onClick={deleteSelectedMeasurements}>
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}

export default DataManagementPage;

const useStyles = makeStyles((theme) => ({
  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    position: "fixed",
    zIndex: "999",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  measurementsTableContainer: {
    height: "900px",
    backgroundColor: "white",
    padding: "16px",
    borderRadius: "8px",
  },

  selectionBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 14px",
    marginBottom: "8px",
    backgroundColor: "#EEF2FF",
    borderRadius: "6px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  selectionBanner__link: {
    background: "none",
    border: "none",
    padding: 0,
    color: "var(--color-primary)",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": { opacity: 0.75 },
  },

  dialog: { margin: "auto" },

  dialogPaper: {
    width: "320px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    lineHeight: "100% !important",
    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: "3px !important",
    height: "25px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    "&:hover": { backgroundColor: "#D84949", color: "#F5F5F5" },
    "&:focus-visible": { outline: "#4654FB auto 1px" },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    margin: "0px 0px 8px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "8px",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    margin: "0px !important",
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  iconSpin: { animation: "$spin 0.7s linear infinite" },

  deleteButtonWrapper: {
    maxWidth: 0,
    opacity: 0,
    overflow: "hidden",
    marginLeft: "-8px",
    transition: "max-width 0.25s ease-in-out, opacity 0.2s ease-in-out, margin-left 0.25s ease-in-out",
  },

  "deleteButtonWrapper__visible": {
    maxWidth: "200px",
    opacity: 1,
    marginLeft: "0px",
  },

  refreshTimestamp: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#9CA3AF",
    whiteSpace: "nowrap",
  },
}));
